import { useAuth } from '@/contexts/AuthContext'
import type { Plan } from '@/types/auth'

export type SubscriptionState = {
  /** Whether the user may create/launch new studies. */
  hasActiveAccess: boolean
  /** Trial or time-boxed plan ran out — show the upgrade banner, block new studies. */
  isExpired: boolean
  plan: Plan | undefined
  /** Whole days until access expires; null when no expiry is set. */
  daysLeft: number | null
  /** Why a renewal is not going through, if it is not (Z-74). */
  paymentState: '' | 'failed' | 'action_required'
  /** Days since the payment problem started; null when there is none. */
  paymentProblemDays: number | null
}

/**
 * Subscription state derived from the current user (`/accounts/me/`).
 * The UI fails open when the backend doesn't send the fields yet — the
 * backend permission layer is the actual enforcement.
 */
export function useSubscription(): SubscriptionState {
  const { user } = useAuth()
  const hasActiveAccess = user?.has_active_access !== false
  const paymentState = user?.payment_state || ''
  const since = user?.payment_state_since
  const paymentProblemDays =
    paymentState && since
      ? Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 86_400_000))
      : null

  return {
    hasActiveAccess,
    isExpired: !hasActiveAccess,
    plan: user?.plan,
    daysLeft: user?.access_days_left ?? null,
    paymentState,
    paymentProblemDays,
  }
}
